import React, { useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  listarMoedasFortes,
  simularCorrecaoTitulos,
  efetivarCorrecaoTitulos,
  simulacaoGetGruposEmpresa,
  listarContasPorGE,
} from '~/Services/Service';
import FormParametros from './components/FormParametros';
import GridSimulacao from './components/GridSimulacao';

const normalizarResposta = res => (res && res.data ? res.data : res);

const CorrecaoTitulos = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [contas, setContas] = useState([]);
  const [moedas, setMoedas] = useState([]);

  const [codigoGrupoEmpresa, setCodigoGrupoEmpresa] = useState(null);
  const [codigoConta, setCodigoConta] = useState(null);
  const [codigoMoedaForte, setCodigoMoedaForte] = useState(null);

  const [simulacao, setSimulacao] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [efetivando, setEfetivando] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const [resGes, resMoedas] = await Promise.all([
          simulacaoGetGruposEmpresa(),
          listarMoedasFortes(),
        ]);
        if (!ativo) return;
        setGruposEmpresa(normalizarResposta(resGes) || []);
        setMoedas(normalizarResposta(resMoedas) || []);
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (codigoGrupoEmpresa == null) {
      setContas([]);
      setCodigoConta(null);
      return undefined;
    }
    let ativo = true;
    listarContasPorGE(codigoGrupoEmpresa)
      .then(res => {
        if (!ativo) return;
        setContas(normalizarResposta(res) || []);
      })
      .catch(err => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => {
      ativo = false;
    };
  }, [codigoGrupoEmpresa]);

  const handleGEChange = useCallback(valor => {
    setCodigoGrupoEmpresa(valor);
    setCodigoConta(null);
    setSimulacao(null);
  }, []);

  const handleSimular = useCallback(async () => {
    if (
      codigoGrupoEmpresa == null ||
      codigoConta == null ||
      codigoMoedaForte == null
    ) {
      toast.warn('Selecione GE, Conta e Moeda');
      return;
    }
    setCarregando(true);
    setSimulacao(null);
    try {
      const res = await simularCorrecaoTitulos(
        codigoGrupoEmpresa,
        codigoConta,
        codigoMoedaForte
      );
      const dados = normalizarResposta(res) || {};
      if (!dados.titulos || dados.titulos.length === 0) {
        toast.info(
          'Nenhum título elegível encontrado para os parâmetros informados.'
        );
      }
      setSimulacao(dados);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao simular correção';
      if (
        msg.toLowerCase().includes('cotação') ||
        msg.toLowerCase().includes('cotacao')
      ) {
        toast.error('Cotação não encontrada para a moeda na data');
      } else {
        toast.error(msg);
      }
    } finally {
      setCarregando(false);
    }
  }, [codigoGrupoEmpresa, codigoConta, codigoMoedaForte]);

  const handleAbrirModal = useCallback(() => {
    if (!simulacao || !simulacao.titulos || simulacao.titulos.length === 0) {
      toast.warn('Execute a simulação antes de efetivar.');
      return;
    }
    setShowModal(true);
  }, [simulacao]);

  const handleEfetivar = useCallback(async () => {
    setShowModal(false);
    setEfetivando(true);
    try {
      const res = await efetivarCorrecaoTitulos({
        codigo_grupo_empresa: codigoGrupoEmpresa,
        codigo_conta: codigoConta,
        codigo_moeda_forte: codigoMoedaForte,
        confirmado: true,
      });
      const dados = normalizarResposta(res) || {};
      const totalFormatado = (dados.total_corrigido || 0).toLocaleString(
        'pt-BR',
        {
          style: 'currency',
          currency: 'BRL',
        }
      );
      toast.success(
        `${dados.total_titulos_corrigidos ||
          0} título(s) corrigido(s). Total: ${totalFormatado}.`
      );
      setSimulacao(null);
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao efetivar correção';
      toast.error(msg);
    } finally {
      setEfetivando(false);
    }
  }, [codigoGrupoEmpresa, codigoConta, codigoMoedaForte]);

  const handleFecharModal = useCallback(
    confirmado => {
      if (confirmado) {
        handleEfetivar();
        return;
      }
      setShowModal(false);
    },
    [handleEfetivar]
  );

  const totalTitulosConfirmacao = simulacao ? simulacao.total_titulos || 0 : 0;
  const totalCorrigidoConfirmacao = simulacao
    ? simulacao.soma_valor_corrigido || 0
    : 0;
  const totalCorrigidoFormatado = Number(
    totalCorrigidoConfirmacao
  ).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
  const mensagemConfirmacao =
    `Esta operação aplicará a correção e é irreversível. ` +
    `${totalTitulosConfirmacao} título(s) serão corrigidos. ` +
    `Total: ${totalCorrigidoFormatado}. Confirma?`;

  return (
    <Container fluid className="py-3">
      <Header title="Correção de Títulos a Pagar" />
      {(carregando || efetivando) && <Loading />}

      <FormParametros
        gruposEmpresa={gruposEmpresa}
        contas={contas}
        moedas={moedas}
        codigoGrupoEmpresa={codigoGrupoEmpresa}
        codigoConta={codigoConta}
        codigoMoedaForte={codigoMoedaForte}
        onGEChange={handleGEChange}
        onContaChange={setCodigoConta}
        onMoedaChange={setCodigoMoedaForte}
        onSimular={handleSimular}
        carregando={carregando}
      />

      {simulacao && (
        <GridSimulacao
          titulos={simulacao.titulos || []}
          somaValorAtual={simulacao.soma_valor_atual || 0}
          somaValorCorrigido={simulacao.soma_valor_corrigido || 0}
          totalTitulos={simulacao.total_titulos || 0}
          onEfetivar={handleAbrirModal}
          efetivando={efetivando}
        />
      )}

      <ModalConfirmacao
        show={showModal}
        onClose={handleFecharModal}
        title="Confirmar Efetivação"
        message={mensagemConfirmacao}
        variant="danger"
      />
    </Container>
  );
};

export default CorrecaoTitulos;
